import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import SectionCard from '@/components/common/SectionCard/SectionCard';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';
import useGetContractDetails from '@/hooks/contract/useGetContractDetails.hook';
import { NumberUtils } from '@/utils/numbers.utils';
import useGetContractUpliftCaps from './hooks/useGetContractUpliftCaps';
import useUpsertContractUpliftCaps, { UpsertUpliftCapsInput } from './hooks/useUpsertContractUpliftCaps';
import UpliftCapsForm, { UpliftCapsFormValues, UpliftCapFormValues } from './components/UpliftCapsForm';
import {
  UpliftModifierEnum,
  UpliftCapRateTypeEnum,
  UpliftCap,
} from './upliftCaps.types';

const getDefaultCapValues = (): UpliftCapFormValues => ({
  modifier1: UpliftModifierEnum.None,
  endDate: null,
  rate1Type: UpliftCapRateTypeEnum.Index,
  rate1IndexId: null,
  rate1FixedValue: null,
  rate2Type: UpliftCapRateTypeEnum.Index,
  rate2IndexId: null,
  rate2FixedValue: null,
  modifier2: UpliftModifierEnum.None,
  rate3Type: UpliftCapRateTypeEnum.Index,
  rate3IndexId: null,
  rate3FixedValue: null,
  calculatedCapPercentage: null,
});

const mapCapToFormValues = (cap: UpliftCap | null): UpliftCapFormValues | null => {
  if (!cap) return null;

  return {
    modifier1: cap.modifier1,
    endDate: cap.endDate ? new Date(cap.endDate) : null,
    rate1Type: cap.rate1?.type ?? UpliftCapRateTypeEnum.Index,
    rate1IndexId: cap.rate1?.indexId ?? null,
    rate1FixedValue: cap.rate1?.fixedValue ?? null,
    rate2Type: cap.rate2?.type ?? UpliftCapRateTypeEnum.Index,
    rate2IndexId: cap.rate2?.indexId ?? null,
    rate2FixedValue: cap.rate2?.fixedValue ?? null,
    modifier2: cap.modifier2 ?? UpliftModifierEnum.None,
    rate3Type: cap.rate3?.type ?? UpliftCapRateTypeEnum.Index,
    rate3IndexId: cap.rate3?.indexId ?? null,
    rate3FixedValue: cap.rate3?.fixedValue ?? null,
    calculatedCapPercentage: cap.calculatedCapPercentage ?? null,
  };
};

export default function UpliftCapsContainer() {
  const sectionCardRef = useRef<any>();
  const { contractId }: { contractId: UUID } = useParams();
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();

  const { contract } = useGetContractDetails(contractId);
  const { upliftCaps, isLoading } = useGetContractUpliftCaps(contractId);
  const { upsertContractUpliftCaps, isLoadingUpsert } = useUpsertContractUpliftCaps(contractId);

  useEffect(() => {
    const getRole = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };

    getRole();
  }, [getUserRole]);

  const isEditable = userRole === UserRole.ADMIN || userRole === UserRole.FULL_ACCESS;

  const formValues: UpliftCapsFormValues = {
    existingUpliftCap: upliftCaps?.existingUpliftCap
      ? mapCapToFormValues(upliftCaps.existingUpliftCap)
      : getDefaultCapValues(),
    furtherUpliftCap: upliftCaps?.furtherUpliftCap
      ? mapCapToFormValues(upliftCaps.furtherUpliftCap)
      : getDefaultCapValues(),
  };

  const handleSubmit = (values: UpliftCapsFormValues) => {
    const mapCapFormToInput = (cap: UpliftCapFormValues | null) => {
      if (!cap) return null;

      const showRate2 = cap.modifier1 !== UpliftModifierEnum.None
        && cap.modifier1 !== UpliftModifierEnum.Equals;
      const showRate3 = cap.modifier2 === UpliftModifierEnum.MaximumOf;

      return {
        modifier1: cap.modifier1,
        endDate: cap.endDate?.toISOString() ?? null,
        rate1: {
          type: cap.rate1Type,
          indexId: cap.rate1Type === UpliftCapRateTypeEnum.Index ? cap.rate1IndexId : null,
          fixedValue: cap.rate1Type === UpliftCapRateTypeEnum.Fixed ? cap.rate1FixedValue : null,
        },
        rate2: showRate2 ? {
          type: cap.rate2Type ?? UpliftCapRateTypeEnum.Index,
          indexId: cap.rate2Type === UpliftCapRateTypeEnum.Index ? cap.rate2IndexId : null,
          fixedValue: cap.rate2Type === UpliftCapRateTypeEnum.Fixed ? cap.rate2FixedValue : null,
        } : null,
        modifier2: cap.modifier2,
        rate3: showRate3 ? {
          type: cap.rate3Type ?? UpliftCapRateTypeEnum.Index,
          indexId: cap.rate3Type === UpliftCapRateTypeEnum.Index ? cap.rate3IndexId : null,
          fixedValue: cap.rate3Type === UpliftCapRateTypeEnum.Fixed ? cap.rate3FixedValue : null,
        } : null,
      };
    };

    const input: UpsertUpliftCapsInput = {
      existingUpliftCap: mapCapFormToInput(values.existingUpliftCap),
      furtherUpliftCap: mapCapFormToInput(values.furtherUpliftCap),
    };

    upsertContractUpliftCaps(input);
  };

  return (
    <div className="mb-20">
      <SectionCard
        ref={sectionCardRef}
        title={{
          titleText: 'Uplift Caps',
        }}
        padding={true}
      >
        <UpliftCapsForm
          initialValues={formValues}
          isLoading={isLoading}
          isLoadingUpdate={isLoadingUpsert}
          isEditable={isEditable}
          onSubmit={handleSubmit}
          customerName={contract?.customer.customerName ?? ''}
          customerCode={contract?.customer.customerAccountNumber ?? ''}
          contractNumber={contract?.contractNumber ?? ''}
          startDate={contract?.contractStartDate ?? ''}
          endDate={contract?.contractEndDate ?? ''}
          totalValue={contract
            ? `${contract.currency.currency} ${NumberUtils.prettifyWithRound(contract.totalContractValue)}`
            : ''}
          nextUpliftDate={contract?.nextUpliftDate ?? null}
        />
      </SectionCard>
    </div>
  );
}
